import { CustomState } from "@iewcasia/Store/Reducers";
import { CustomCatalogSetting } from "@iewcasia/Widgets/CheckoutReviewAndSubmit/CheckoutReviewAndSubmitShippingDetails";
import { getCookie } from "@insite/client-framework/Common/Cookies";
import { createFromProduct } from "@insite/client-framework/Common/ProductInfo";
import { addToChainAfter, executeAwaitableHandlerChain, replaceInChain } from "@insite/client-framework/HandlerCreator";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import loadCatalogPageByPath from "@insite/client-framework/Store/Data/CatalogPages/Handlers/LoadCatalogPageByPath";
import {
    chain,
    LoadRealTimePricingAndInventory,
    ParseQueryParameter,
    PopulateApiParameter,
    PrepageProductRecs,
    RequestCatalogPageFromApi,
    SetExpandTokens,
    SetUpProductInfos,
} from "@insite/client-framework/Store/Pages/ProductList/Handlers/DisplayProducts";
import loadProductsPricingAndInventory from "@insite/client-framework/Store/Pages/ProductList/Handlers/LoadProductsPricingAndInventory";
import qs from "qs";

let view = "List";
let tablePage = 1;
/** Bumped for every table DisplayProducts run so late infinite-scroll responses can be ignored. */
let tableListRequestSeq = 0;

type TableRequestProps = {
    tableRequestId?: number;
    tableRequestPage?: number;
};

function initializeView(catalogPage: any) {
    if (
        catalogPage &&
        (catalogPage.properties?.productAttributes?.length > 0 || catalogPage.properties?.productProperties?.length > 0)
    ) {
        view = "Table";
    } else {
        view = "List";
    }
}

replaceInChain(chain, RequestCatalogPageFromApi, async props => {
    const { path, isSearch } = props.parameter;
    props.result = {
        productFilters: {},
        isFiltered: false,
        productInfosByProductId: {},
    };

    if (path && !isSearch) {
        props.result.catalogPage = getCatalogPageStateByPath(props.getState(), path).value;
        if (props.result.catalogPage) {
            initializeView(props.result.catalogPage);
            return;
        }
        props.result.catalogPage = await executeAwaitableHandlerChain(loadCatalogPageByPath, { path }, props);
        initializeView(props.result.catalogPage);
    }
});

addToChainAfter(chain, SetExpandTokens, props => {
    // Verndale Customization
    const state = props.getState() as CustomState;
    props.expandTokens?.push("properties", "documents");

    if (state.customProductList.page) {
        props.expandTokens = props.expandTokens?.filter(token => token !== "variantTraits");
    }
    // END Verndale Customization
});

replaceInChain(chain, ParseQueryParameter, props => {
    const state = props.getState() as CustomState;
    const settingsCollection = getSettingsCollection(state);
    const customCatalogSettings = settingsCollection?.["customCatalogSettings"] as CustomCatalogSetting;

    const { queryString } = props.parameter;
    const currentFilters = state.pages.productList.productFilters;
    const parsedQuery = qs.parse(queryString?.startsWith("?") ? queryString?.substr(1) : queryString || "") as any;

    const splitCommaSeparated = (value?: string | string[] | null) =>
        value && typeof value === "string" ? value.split(",") : undefined;
    const splitSpaceSeparated = (value?: string | string[] | null) =>
        value && typeof value === "string" ? value.split(" ") : undefined;

    const {
        criteria,
        query,
        page,
        sort,
        includeSuggestions,
        stockedItemsOnly,
        previouslyPurchasedProducts,
        categoryId,
        brandIds,
        productLineIds,
        priceFilters,
        attributeValueIds,
        searchWithinQueries,
    } = parsedQuery;

    const { catalogPage } = props.result;

    const newQuery = criteria || query;
    const hasQueryChanged = newQuery && currentFilters.query && newQuery !== currentFilters.query;

    // Treat first selection of a sort (previous undefined) as a change so table infinite-scroll resets
    const sortChanged = !!sort && sort !== currentFilters.sort;
    const filtersChanged =
        (categoryId && categoryId !== currentFilters.categoryId) ||
        (stockedItemsOnly !== undefined && stockedItemsOnly !== String(currentFilters.stockedItemsOnly)) ||
        (previouslyPurchasedProducts !== undefined &&
            previouslyPurchasedProducts !== String(currentFilters.previouslyPurchasedProducts)) ||
        (brandIds && brandIds !== currentFilters.brandIds?.join(",")) ||
        (productLineIds && productLineIds !== currentFilters.productLineIds?.join(",")) ||
        (priceFilters && priceFilters !== currentFilters.priceFilters?.join(",")) ||
        (attributeValueIds && attributeValueIds !== currentFilters.attributeValueIds?.join(","));

    const shouldResetTable = view === "Table" && (sortChanged || filtersChanged || hasQueryChanged);

    if (shouldResetTable) {
        tablePage = 1;
        props.dispatch({
            type: "Custom/ProductList/ResetInfiniteScrollPage",
        } as any);
    } else if (view === "Table" && parsedQuery.page) {
        // Infinite-scroll page only — never apply &page=N on top of a sort/filter reset.
        tablePage = !Number.isNaN(Number(parsedQuery.page)) ? parseInt(parsedQuery.page as string, 10) : 1;

        props.dispatch({
            type: "Custom/ProductList/SetPage",
            page: tablePage,
        } as any);
    } else if (view === "Table") {
        // URL without page (category load / sort) always starts at page 1.
        tablePage = 1;
    }

    if (view === "Table") {
        tableListRequestSeq += 1;
        const tableProps = props as typeof props & TableRequestProps;
        tableProps.tableRequestId = tableListRequestSeq;
        tableProps.tableRequestPage = tablePage;
    }

    const catalogPageSize =
        view === "Table"
            ? customCatalogSettings?.["categoriesTablePageSize"]
            : (getCookie("ProductList-PageSize") as string) || customCatalogSettings?.["categoriesPageSize"];

    let pageSize = catalogPageSize || parsedQuery.pageSize;

    if (!pageSize) {
        pageSize = getCookie("ProductList-PageSize");
    }

    const directQueryParam = props.parameter.query;
    const queryFromQueryString = criteria || query;
    const queryFromParams = directQueryParam || queryFromQueryString;
    const isLoadingMorePages = page && parseInt(page as string, 10) > 1;
    const shouldPreserveQuery =
        queryString === undefined && view === "Table" && isLoadingMorePages && currentFilters.query;

    const finalQuery = queryFromParams || (shouldPreserveQuery ? currentFilters.query : undefined);

    props.result.productFilters = {
        query: finalQuery,
        page: view === "Table" ? undefined : page ? parseInt(page as string, 10) : undefined,
        pageSize: view === "Table" ? undefined : parseInt(pageSize as string, 10),
        sort,
        includeSuggestions: includeSuggestions !== undefined ? includeSuggestions === "true" : undefined,
        stockedItemsOnly: stockedItemsOnly !== undefined ? stockedItemsOnly === "true" : undefined,
        previouslyPurchasedProducts:
            previouslyPurchasedProducts !== undefined ? previouslyPurchasedProducts === "true" : undefined,
        pageCategoryId: catalogPage?.categoryId,
        pageBrandId: catalogPage?.brandId || undefined,
        pageProductLineId: catalogPage?.productLineId || undefined,
        categoryId,
        brandIds: splitCommaSeparated(brandIds),
        productLineIds: splitCommaSeparated(productLineIds),
        priceFilters: splitCommaSeparated(priceFilters),
        attributeValueIds: splitCommaSeparated(attributeValueIds),
        searchWithinQueries: splitSpaceSeparated(searchWithinQueries), // Search within Queries need to split by space, to use for ElasticSearch
    };
});

replaceInChain(chain, PopulateApiParameter, props => {
    const state = props.getState() as CustomState;

    const settingsCollection = getSettingsCollection(state);
    const customCatalogSettings = settingsCollection?.["customCatalogSettings"] as CustomCatalogSetting;

    const catalogPageSize =
        view === "Table"
            ? customCatalogSettings?.["categoriesTablePageSize"]
            : customCatalogSettings?.["categoriesPageSize"];

    const filters = props.result.productFilters;
    const tableProps = props as typeof props & TableRequestProps;
    const requestPage = tableProps.tableRequestPage ?? tablePage;

    const {
        query,
        brandIds,
        productLineIds,
        priceFilters,
        attributeValueIds,
        searchWithinQueries,
        pageCategoryId,
        pageBrandId,
        pageProductLineId,
        ...apiParameter
    } = filters;
    props.apiParameter = {
        ...apiParameter,
        page: view === "Table" ? requestPage : filters.page,
        pageSize: view === "Table" ? +catalogPageSize : filters.pageSize,
        search: query,
        categoryId: filters.categoryId || filters.pageCategoryId,
        searchWithin: searchWithinQueries?.join(" "),
        includeSuggestions: filters.includeSuggestions !== false,
        brandIds: filters.pageBrandId ? [filters.pageBrandId] : brandIds,
        productLineIds: filters.pageProductLineId ? [filters.pageProductLineId] : productLineIds,
        priceFilters,
        attributeValueIds,
        expand: props.expandTokens || [],
        applyPersonalization: true,
        includeAttributes: ["includeOnProduct"],
        relevancy: props.parameter.relevancy,
    };
});

replaceInChain(chain, SetUpProductInfos, props => {
    // Verndale Customization
    const state = props.getState() as CustomState;
    const tableProps = props as typeof props & TableRequestProps;
    const requestPage = tableProps.tableRequestPage ?? tablePage;

    // Preserve pricing/inventory already loaded for earlier pages when appending.
    if (view === "Table" && requestPage > 1 && state.customProductList.productInfosByProductId) {
        props.result.productInfosByProductId = { ...state.customProductList.productInfosByProductId };
    }
    // END Verndale Customization

    const categoryPath = props.apiParameter.search ? undefined : props.result.catalogPage?.canonicalPath;

    props.idByPath = {};
    props.result.products?.forEach(product => {
        const productDetailPath = categoryPath ? `${categoryPath}/${product.urlSegment}` : product.canonicalUrl;
        props.idByPath![productDetailPath] = product.id;

        if (!props.result.productInfosByProductId[product.id]) {
            props.result.productInfosByProductId[product.id] = {
                ...createFromProduct(product),
                productDetailPath,
            };
        } else {
            props.result.productInfosByProductId[product.id] = {
                ...props.result.productInfosByProductId[product.id]!,
                productDetailPath,
            };
        }
    });
});

replaceInChain(chain, LoadRealTimePricingAndInventory, props => {
    // Verndale Customization — unchanged: only loads pricing/inventory for products missing it
    props.dispatch(
        loadProductsPricingAndInventory({
            products: props.result.products,
            productInfosByProductId: props.result.productInfosByProductId,
        }),
    );
});

addToChainAfter(chain, PrepageProductRecs, props => {
    // Verndale Customization
    const tableProps = props as typeof props & TableRequestProps;

    if (view === "Table") {
        // Ignore stale infinite-scroll responses that finished after a newer sort/filter request.
        const isStale =
            tableProps.tableRequestId !== undefined && tableProps.tableRequestId !== tableListRequestSeq;

        if (!isStale) {
            const requestPage = tableProps.tableRequestPage ?? tablePage;

            props.dispatch({
                type: "Custom/ProductList/SetProducts",
                products: props.result.products,
                productInfosByProductId: props.result.productInfosByProductId,
                // Page 1 (initial load or sort/filter reset) replaces; page 2+ appends.
                replace: requestPage <= 1,
            } as any);
        }

        props.dispatch({
            type: "Custom/ProductList/InfiniteScrollLoaded",
        } as any);
    }

    props.dispatch({
        type: "Custom/ProductList/SetFilterLoading",
        isLoadingFilter: false,
    } as any);

    // END Verndale Customization
});
